import React from 'react';
import { Monitor, Laptop, Printer, Wifi, HardDrive, Server, Keyboard, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const ITServices = () => {
  const { language } = useLanguage();

  const products = [
    {
      icon: Monitor,
      title: { fr: 'Ordinateurs de Bureau', en: 'Desktop Computers' },
      description: { fr: 'Postes de travail performants pour tous vos besoins', en: 'High-performance workstations for all your needs' },
      image: 'https://images.unsplash.com/photo-1587831990711-23ca6441447b',
      specs: [
        { fr: 'Processeurs Intel Core i5/i7/i9', en: 'Intel Core i5/i7/i9 processors' },
        { fr: 'RAM 8GB à 64GB', en: 'RAM 8GB to 64GB' },
        { fr: 'SSD 256GB à 2TB', en: 'SSD 256GB to 2TB' },
        { fr: 'Écrans 22" à 32"', en: 'Screens 22" to 32"' },
        { fr: 'Garantie 1 à 3 ans', en: '1 to 3 year warranty' }
      ]
    },
    {
      icon: Laptop,
      title: { fr: 'Ordinateurs Portables', en: 'Laptops' },
      description: { fr: 'Mobilité et performance pour vos déplacements', en: 'Mobility and performance for your travels' },
      image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853',
      specs: [
        { fr: 'Ultrabooks légers et puissants', en: 'Lightweight and powerful ultrabooks' },
        { fr: 'Autonomie 8h à 12h', en: '8h to 12h battery life' },
        { fr: 'Écrans Full HD et 4K', en: 'Full HD and 4K screens' },
        { fr: 'Touchscreen disponible', en: 'Touchscreen available' },
        { fr: 'Windows et macOS', en: 'Windows and macOS' }
      ]
    },
    {
      icon: Printer,
      title: { fr: 'Imprimantes et Scanners', en: 'Printers and Scanners' },
      description: { fr: 'Solutions d\'impression professionnelles', en: 'Professional printing solutions' },
      image: 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6',
      specs: [
        { fr: 'Imprimantes multifonctions', en: 'Multifunction printers' },
        { fr: 'Jet d\'encre et laser', en: 'Inkjet and laser' },
        { fr: 'Impression couleur et N&B', en: 'Color and B&W printing' },
        { fr: 'Réseau WiFi et Ethernet', en: 'WiFi and Ethernet network' },
        { fr: 'Scan recto-verso automatique', en: 'Automatic duplex scanning' }
      ]
    },
    {
      icon: Wifi,
      title: { fr: 'Équipements Réseau', en: 'Network Equipment' },
      description: { fr: 'Infrastructure réseau fiable et sécurisée', en: 'Reliable and secure network infrastructure' },
      image: 'https://images.unsplash.com/photo-1606904825846-647eb07f5be2',
      specs: [
        { fr: 'Routeurs professionnels', en: 'Professional routers' },
        { fr: 'Switches gérés et non-gérés', en: 'Managed and unmanaged switches' },
        { fr: 'Points d\'accès WiFi 6', en: 'WiFi 6 access points' },
        { fr: 'Pare-feu et sécurité', en: 'Firewalls and security' },
        { fr: 'Configuration et installation', en: 'Configuration and installation' }
      ]
    },
    {
      icon: HardDrive,
      title: { fr: 'Stockage et Sauvegarde', en: 'Storage and Backup' },
      description: { fr: 'Solutions de stockage et protection de données', en: 'Data storage and protection solutions' },
      image: 'https://images.unsplash.com/photo-1531492746076-161ca9bcad58',
      specs: [
        { fr: 'Disques durs externes 1TB à 10TB', en: 'External hard drives 1TB to 10TB' },
        { fr: 'SSD externes ultra-rapides', en: 'Ultra-fast external SSDs' },
        { fr: 'Clés USB 16GB à 256GB', en: 'USB drives 16GB to 256GB' },
        { fr: 'NAS pour entreprise', en: 'NAS for enterprise' },
        { fr: 'Solutions de backup cloud', en: 'Cloud backup solutions' }
      ]
    },
    {
      icon: Monitor,
      title: { fr: 'Écrans et Moniteurs', en: 'Screens and Monitors' },
      description: { fr: 'Affichage de qualité professionnelle', en: 'Professional quality display' },
      image: 'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf',
      specs: [
        { fr: 'Écrans 24" à 34"', en: 'Screens 24" to 34"' },
        { fr: 'Résolutions Full HD à 4K', en: 'Full HD to 4K resolutions' },
        { fr: 'Écrans incurvés disponibles', en: 'Curved screens available' },
        { fr: 'Support ergonomique réglable', en: 'Adjustable ergonomic stand' },
        { fr: 'Connexions HDMI, DisplayPort, USB-C', en: 'HDMI, DisplayPort, USB-C connections' }
      ]
    },
    {
      icon: Keyboard,
      title: { fr: 'Périphériques', en: 'Peripherals' },
      description: { fr: 'Accessoires pour productivité optimale', en: 'Accessories for optimal productivity' },
      image: 'https://images.unsplash.com/photo-1587829741301-dc798b83add3',
      specs: [
        { fr: 'Claviers mécaniques et membranes', en: 'Mechanical and membrane keyboards' },
        { fr: 'Souris ergonomiques', en: 'Ergonomic mice' },
        { fr: 'Webcams HD et 4K', en: 'HD and 4K webcams' },
        { fr: 'Casques audio professionnels', en: 'Professional audio headsets' },
        { fr: 'Hubs USB et adaptateurs', en: 'USB hubs and adapters' }
      ]
    },
    {
      icon: Server,
      title: { fr: 'Serveurs et Infrastructure', en: 'Servers and Infrastructure' },
      description: { fr: 'Solutions serveur pour entreprises', en: 'Server solutions for businesses' },
      image: 'https://images.unsplash.com/photo-1639066648921-82d4500abf1a',
      specs: [
        { fr: 'Serveurs rack et tour', en: 'Rack and tower servers' },
        { fr: 'Virtualisation et cloud privé', en: 'Virtualization and private cloud' },
        { fr: 'Stockage en réseau (NAS/SAN)', en: 'Network storage (NAS/SAN)' },
        { fr: 'Redondance et haute disponibilité', en: 'Redundancy and high availability' },
        { fr: 'Installation et maintenance', en: 'Installation and maintenance' }
      ]
    }
  ];

  const services = [
    {
      title: { fr: 'Installation et Configuration', en: 'Installation and Configuration' },
      description: { fr: 'Déploiement complet de votre infrastructure IT', en: 'Complete deployment of your IT infrastructure' }
    },
    {
      title: { fr: 'Support Technique', en: 'Technical Support' },
      description: { fr: 'Assistance 24/7 pour résoudre vos problèmes', en: '24/7 assistance to solve your problems' }
    },
    {
      title: { fr: 'Formation Utilisateurs', en: 'User Training' },
      description: { fr: 'Formation de vos équipes sur les nouveaux équipements', en: 'Training your teams on new equipment' }
    },
    {
      title: { fr: 'Garantie et SAV', en: 'Warranty and After-Sales' },
      description: { fr: 'Service après-vente réactif et garanties étendues', en: 'Responsive after-sales service and extended warranties' }
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1566647387313-9fda80664848)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Laptop className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Informatique & Électronique' : 'IT & Electronics'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Solutions informatiques complètes pour votre entreprise' : 'Complete IT solutions for your business'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Notre Catalogue Produits' : 'Our Product Catalog'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={product.image} alt={product.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{product.description[language]}</p>
                    <ul className="space-y-3">
                      {product.specs.map((spec, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{spec[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Services' : 'Our Services'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Card key={index} className="p-6 text-center bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200 hover:border-blue-500 transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title[language]}</h3>
                <p className="text-gray-600 text-sm">{service.description[language]}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Besoin d\'un Devis ?' : 'Need a Quote?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Contactez-nous pour obtenir une offre personnalisée' : 'Contact us for a personalized offer'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander un Devis' : 'Request a Quote'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ITServices;